import { accumulate, observableSelector } from '../../helpers';
import { concat, Observable, ReplaySubject, Subscription } from 'rxjs';
import { ExtractionResult, ExtractionSelector } from '../../interfaces/functions.interface';
import { finalize, first, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { inject, injectable } from 'inversify';
import { ISessionLifecycle, SESSION_LIFECYCLE_SERVICE } from '../session-lifecycle';
import { ISessionPersistenceService, SESSION_PERSISTENCE_SERVICE } from '../session-persistence';
import { IStateManagerService, IStateObject } from '../../interfaces';

@injectable()
export class SessionStateService implements IStateManagerService {
    @inject(SESSION_LIFECYCLE_SERVICE) private readonly sessionLifeCycle: ISessionLifecycle;
    @inject(SESSION_PERSISTENCE_SERVICE) private readonly sessionPersistence: ISessionPersistenceService;

    private readonly stateChangesObs = new ReplaySubject<Partial<IStateObject>>();

    private _fullStateObs: Observable<IStateObject>;

    private stateSubscription: Subscription;

    private readonly getFullStateObs = (): Observable<IStateObject> =>
        this._fullStateObs ?? (this._fullStateObs =
            concat(
                this.sessionPersistence.stateObservable().pipe(first()),
                this.stateChangesObs,
            ).pipe(
                takeUntil(this.sessionLifeCycle.destroyObservable()),
                finalize(() => delete this._fullStateObs),
                accumulate({} as IStateObject),
                tap(this.sessionPersistence.save),
                shareReplay(1),
            )
        );

    private readonly innerSubscribe = (): Subscription =>
        this.stateSubscription ?? (
            this.stateSubscription = this.getFullStateObs().pipe(
                takeUntil(this.sessionLifeCycle.destroyObservable()),
            ).subscribe());

    stateObservable = <S extends ExtractionSelector<IStateObject>>(request: S):Observable<ExtractionResult<IStateObject, S>> =>
        this.getFullStateObs().pipe(
            observableSelector(request),
        );

    save = <K extends keyof IStateObject>(key: K, value: IStateObject[K]): void =>
        this.innerSubscribe() && this.stateChangesObs.next({ [ key ]: value });

}
